import { BarChartOutlined, DatabaseOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import Markdown from 'react-markdown';
import styled from 'styled-components';
import ChartModal from './ChartModal';
import DatasetModal from './DatasetModal';

const StyledLink = styled(Button)`
  padding: 0 4px;
  height: auto;
  font-size: inherit;
  line-height: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

/**
 * Custom Markdown renderer that converts asset references to clickable links
 *
 * Parses patterns like:
 * - [DATA:result_key] - inline dataset references
 * - [CHART:chart_name] - inline chart references
 * - datasets_used : [key1, key2] - in Assets Used manifest sections
 * - charts_used : [chart1, chart2] - in Assets Used manifest sections
 */
function MarkdownWithAssetLinks({ children = '' }) {
  const [datasetModalOpen, setDatasetModalOpen] = useState(false);
  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [selectedChart, setSelectedChart] = useState(null);

  const handleDatasetClick = useCallback((resultKey) => {
    setSelectedDataset(resultKey);
    setDatasetModalOpen(true);
  }, []);

  const handleChartClick = useCallback((chartName) => {
    setSelectedChart(chartName);
    setChartModalOpen(true);
  }, []);

  const closeDatasetModal = () => {
    setDatasetModalOpen(false);
    setSelectedDataset(null);
  };

  const closeChartModal = () => {
    setChartModalOpen(false);
    setSelectedChart(null);
  };

  // Helper to extract full text from nested children
  const extractFullText = useCallback((childNodes) => {
    if (!childNodes) return '';
    if (typeof childNodes === 'string') return childNodes;
    if (Array.isArray(childNodes)) {
      return childNodes
        .map((child) => {
          if (typeof child === 'string') return child;
          if (React.isValidElement(child) && child.props && child.props.children) {
            return extractFullText(child.props.children);
          }
          return '';
        })
        .join('');
    }
    if (React.isValidElement(childNodes) && childNodes.props && childNodes.props.children) {
      return extractFullText(childNodes.props.children);
    }
    return '';
  }, []);

  // Process a single string to detect asset patterns
  const processStringForAssets = useCallback(
    (text, keyPrefix, fullTextContext = '') => {
      // Use fullTextContext for pattern matching if available (list items)
      // But still use original text for rendering the actual content
      const textForMatching = fullTextContext || text;

      // Patterns to match:
      // 1. [DATA:result_key] - inline data references
      // 2. [CHART:chart_name] - inline chart references
      // 3. datasets_used : [key1, key2] - in manifest sections
      // 4. charts_used : [chart1, chart2] - in manifest sections

      const parts = [];
      let lastIndex = 0;
      let match;

      // Pattern 1: [DATA:identifier] or [DATA:identifier|params]
      // Captures dataset name (before pipe if present) and ignores parameters
      const dataRefPattern = /\[DATA:([a-zA-Z0-9_-]+)(?:\|[^\]]*)?\]/g;
      const dataRefMatches = [];
      // eslint-disable-next-line no-cond-assign
      while ((match = dataRefPattern.exec(text)) !== null) {
        dataRefMatches.push({
          type: 'dataset',
          index: match.index,
          length: match[0].length,
          id: match[1], // This captures only the dataset name before the pipe
          fullMatch: match[0],
        });
      }

      // Pattern 2: [CHART:identifier]
      const chartRefPattern = /\[CHART:([a-zA-Z0-9_-]+)\]/g;
      const chartRefMatches = [];
      // eslint-disable-next-line no-cond-assign
      while ((match = chartRefPattern.exec(text)) !== null) {
        chartRefMatches.push({
          type: 'chart',
          index: match.index,
          length: match[0].length,
          id: match[1],
          fullMatch: match[0],
        });
      }

      // Pattern 3: datasets_used : [key1, key2, ...]
      // We need to match the entire pattern and then extract individual keys
      const datasetListPattern = /datasets_used\s*:\s*\[([^\]]+)\]/gi;
      const datasetListMatches = [];
      // eslint-disable-next-line no-cond-assign
      while ((match = datasetListPattern.exec(textForMatching)) !== null) {
        const listContent = match[1]; // Content inside brackets

        // Check if the current text fragment contains any of the keys
        const keys = listContent
          .split(',')
          .map((k) => k.trim())
          .filter((k) => k.length > 0);

        keys.forEach((key) => {
          // Check if this specific text fragment contains this key
          if (text.includes(key)) {
            const keyIndex = text.indexOf(key);
            if (keyIndex !== -1) {
              datasetListMatches.push({
                type: 'dataset',
                index: keyIndex,
                length: key.length,
                id: key,
                fullMatch: key,
              });
            }
          }
        });
      }

      // Pattern 4: charts_used : [chart1, chart2, ...]
      const chartListPattern = /charts_used\s*:\s*\[([^\]]+)\]/gi;
      const chartListMatches = [];
      // eslint-disable-next-line no-cond-assign
      while ((match = chartListPattern.exec(textForMatching)) !== null) {
        const listContent = match[1]; // Content inside brackets

        // Check if the current text fragment contains any of the keys
        const keys = listContent
          .split(',')
          .map((k) => k.trim())
          .filter((k) => k.length > 0);

        keys.forEach((key) => {
          // Check if this specific text fragment contains this key
          if (text.includes(key)) {
            const keyIndex = text.indexOf(key);
            if (keyIndex !== -1) {
              chartListMatches.push({
                type: 'chart',
                index: keyIndex,
                length: key.length,
                id: key,
                fullMatch: key,
              });
            }
          }
        });
      }

      // Combine and sort all matches by index
      const allMatches = [
        ...dataRefMatches,
        ...chartRefMatches,
        ...datasetListMatches,
        ...chartListMatches,
      ].sort((a, b) => a.index - b.index);

      // Build parts with links
      allMatches.forEach((matchInfo) => {
        // Add text before this match
        if (matchInfo.index > lastIndex) {
          parts.push(text.substring(lastIndex, matchInfo.index));
        }

        // Add the link button
        if (matchInfo.type === 'dataset') {
          parts.push(
            <StyledLink
              key={`${keyPrefix}-dataset-${matchInfo.id}`}
              type="link"
              size="small"
              icon={<DatabaseOutlined />}
              onClick={() => handleDatasetClick(matchInfo.id)}
            >
              {matchInfo.id}
            </StyledLink>,
          );
        } else if (matchInfo.type === 'chart') {
          parts.push(
            <StyledLink
              key={`${keyPrefix}-chart-${matchInfo.id}`}
              type="link"
              size="small"
              icon={<BarChartOutlined />}
              onClick={() => handleChartClick(matchInfo.id)}
            >
              {matchInfo.id}
            </StyledLink>,
          );
        }

        lastIndex = matchInfo.index + matchInfo.length;
      });

      // Add remaining text
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }

      // If no matches found, return original text
      return parts.length > 0 ? parts : text;
    },
    [handleDatasetClick, handleChartClick],
  );

  // Process text to detect and convert asset references
  const processTextForAssets = useCallback(
    (content, fullTextContext = null) => {
      if (!content) return content;

      // Use fullTextContext if provided (for list items where text is split)
      const textToMatch = fullTextContext || (typeof content === 'string' ? content : '');

      // Handle arrays of children
      if (Array.isArray(content)) {
        return content.map((child) => {
          if (typeof child === 'string') {
            return processStringForAssets(child, `text-${child.substring(0, 10)}`, textToMatch);
          }
          // Handle nested React elements (like code elements)
          if (React.isValidElement(child) && child.props && child.props.children) {
            const processedChild = processTextForAssets(child.props.children, textToMatch);
            return React.cloneElement(child, {}, processedChild);
          }
          return child;
        });
      }

      // Handle single string
      if (typeof content === 'string') {
        return processStringForAssets(content, 'text', textToMatch);
      }

      return content;
    },
    [processStringForAssets],
  );

  // Custom renderer for text nodes to detect and convert asset references
  /* eslint-disable react/jsx-props-no-spreading, react/no-unstable-nested-components */
  const components = useMemo(
    () => ({
      p: ({ children: pChildren, ...props }) => {
        const processedChildren = processTextForAssets(pChildren);
        return <p {...props}>{processedChildren}</p>;
      },
      li: ({ children: liChildren, ...props }) => {
        // For list items, we need to reconstruct the full text to match patterns
        const fullText = extractFullText(liChildren);

        // Check if this list item contains asset references
        if (fullText.includes('datasets_used') || fullText.includes('charts_used')) {
          const processedChildren = processTextForAssets(liChildren, fullText);
          return <li {...props}>{processedChildren}</li>;
        }

        const processedChildren = processTextForAssets(liChildren);
        return <li {...props}>{processedChildren}</li>;
      },
      code: ({ children: codeChildren, inline, ...props }) => {
        // Only process inline code, not code blocks
        if (inline) {
          const processedChildren = processTextForAssets(codeChildren);
          return <code {...props}>{processedChildren}</code>;
        }
        return <code {...props}>{codeChildren}</code>;
      },
    }),
    [processTextForAssets, extractFullText],
  );
  /* eslint-enable react/jsx-props-no-spreading, react/no-unstable-nested-components */

  return (
    <>
      <Markdown components={components}>{children}</Markdown>

      <DatasetModal
        resultKey={selectedDataset}
        open={datasetModalOpen}
        onClose={closeDatasetModal}
      />

      <ChartModal chartName={selectedChart} open={chartModalOpen} onClose={closeChartModal} />
    </>
  );
}

MarkdownWithAssetLinks.propTypes = {
  children: PropTypes.string,
};

export default MarkdownWithAssetLinks;
